<template>
  <card-container
    :id="pageId"
    :is-loading="status.isLoading"
    :loading-message="status.spinnerMessage"
    button-location="top"
    @submit="searchServiceProviders()"
  >
    <template #title>
      Service Providers
    </template>

    <template #additional-actions>
      <p-button
        type="purple"
        round
        @click="onAddNewProvider"
      >
        Add New Provider
      </p-button>
    </template>

    <template #top-form>
      <ProviderSearchWrapper :search-condition="searchCondition" />
      <div class="form-submit-button-group">
        <p-button
          type="purple"
          round
          native-type="submit"
          :disabled="isSubmitBtnDisabled"
        >
          Search
        </p-button>

        <p-button
          type="yellow"
          round
          :disabled="status.isLoading"
          @click="resetForm"
        >
          Reset
        </p-button>
      </div>

      <div class="row align-items-center mt-4">
        <pagination
          :is-loading="status.isLoading"
          :current-page="status.currentPage"
          :total-pages="status.totalPages"
          @load-page-data="searchServiceProviders"
        />
      </div>
    </template>

    <template #content>
      <div class="custom-collapse">
        <el-collapse
          v-model="activeCollapseId"
          accordion
        >
          <HomeServiceProviderCardItem
            v-for="(item, index) in providers"
            :key="index"
            :provider="item"
            :isActive="activeCollapseId === item.id"
            @edit="onEditProvider(index)"
            @deactivate="onDeactivateProvider(index)"
          />
        </el-collapse>
      </div>
    </template>
  </card-container>
</template>

<script setup lang="ts">
import { ref, reactive, computed, Ref, defineAsyncComponent } from 'vue';

import { useRouter } from 'vue-router';

import util from '@/Utils/util';
import { useSwalDialog } from '@/use/swalDialog';
import { catchErrorMessageBox } from '@/Utils/catchErrorMessageBox';
import { HomeServiceProviderSearchType, HomeServiceType, HomeServiceProviderQueryInput, HomeServiceProvider } from '@/models/homeService';
import { homeServiceOrderService } from '@/services/homeServiceOrder';

const ProviderSearchWrapper = defineAsyncComponent(() => import('./Provider/ProviderSearchWrapper.vue') as any);
const HomeServiceProviderCardItem = defineAsyncComponent(() => import('./Provider/HomeServiceProviderCardItem.vue') as any);

const pageId = 'home-service-providers';
const paginationLimit = 10;

const router = useRouter();
const { showSuccessPopupDialog, showConfirmPopupDialog } = useSwalDialog();

const providers: Ref<HomeServiceProvider[]> = ref([]);
const searchCondition = ref({
  searchType: HomeServiceProviderSearchType.All,
  query: '',
  serviceType: '',
  state: '',
  city: '',
  status: '',
});

const status = reactive({
  isLoading: false,
  spinnerMessage: '',
  totalPages: 1,
  currentPage: 1,
});
const activeCollapseId = ref(0);

const isPhoneNumberValid = computed(() => util.isPhoneNumberValid(searchCondition.value.query));

const isSubmitBtnDisabled = computed(() => {
  if (status.isLoading) {
    return true;
  }

  if (isPhoneNumberFieldVisible.value && !isPhoneNumberValid.value) {
    return true;
  }

  return false;
});

const isSearchValueFieldVisible = computed(() => searchCondition.value.searchType !== HomeServiceProviderSearchType.All);
const isPhoneNumberFieldVisible = computed(() => searchCondition.value.searchType === HomeServiceProviderSearchType.PhoneNumber);

const searchServiceProviders = async (currentPage: number = 1) => {
  const { searchType, state, city, query, serviceType, status: providerStatus } = searchCondition.value;

  const offset = (currentPage - 1) * paginationLimit;
  status.currentPage = currentPage;
  status.spinnerMessage = 'Loading Providers...';
  status.isLoading = true;

  const payload: HomeServiceProviderQueryInput = {
    searchType: searchType as HomeServiceProviderSearchType,
    pagination: {
      limit: paginationLimit,
      offset,
    },
  };

  if (state) {
    payload.state = state;
  }

  if (city) {
    payload.city = city;
  }

  if (isSearchValueFieldVisible.value && query) {
    payload.searchValue = query;
  }

  if (serviceType) {
    payload.serviceType = serviceType as HomeServiceType;
  }

  if (providerStatus === 'active') {
    payload.isActive = true;
  } else if (providerStatus === 'inactive') {
    payload.isActive = false;
  }

  catchErrorMessageBox(async () => {
    const response = await homeServiceOrderService.fetchHomeServiceProviders(payload);

    status.totalPages = Math.ceil(response.count / paginationLimit) || 1;
    providers.value = response.rows.map((item: HomeServiceProvider) => item);
    activeCollapseId.value = 0;
  })
    .finally(() => (status.isLoading = false))
    .catch(() => {
      status.totalPages = 1;
      status.currentPage = 1;
    });
};

const onAddNewProvider = () => {
  router.push({
    name: 'CreateUpdateProvider',
  });
};

const onEditProvider = (index: number) => {
  router.push({
    name: 'CreateUpdateProvider',
    params: {
      id: providers.value[index].id,
    },
  });
};

const onDeactivateProvider = async (index: number) => {
  const confirmed = await showConfirmPopupDialog({
    title: 'Are you sure to deactivate the provider?',
  });

  if (!confirmed) {
    return;
  }

  status.spinnerMessage = 'Deactivating Provider...';
  status.isLoading = true;
  catchErrorMessageBox(async () => {
    const payload = {
      isActive: false,
      rates: providers.value[index].rates?.map((item) => ({
        id: item.id,
        serviceType: item.serviceType,
        hourlyRate: item.hourlyRate,
      })),
    };

    await homeServiceOrderService.updateHomeServiceProvider({
      id: providers.value[index].id,
      data: payload,
    });

    await showSuccessPopupDialog({
      text: 'Provider has been deactivated successfully!',
    });
  })
    .finally(() => (status.isLoading = false))
    .catch(() => null);
};

const resetForm = () => {
  searchCondition.value = {
    searchType: HomeServiceProviderSearchType.All,
    query: '',
    serviceType: '',
    state: '',
    city: '',
    status: '',
  };

  providers.value = [];
};
</script>
